import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Grid,
  HStack,
  Icon,
  Progress,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  Award,
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Download,
  Lightbulb,
  Table2,
} from 'lucide-react';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  downloadMappingDocumentCsv,
  groupByProperty,
  type MappingDocumentData,
} from '../coverageMatrixSectionUtils';

type CoverageMatrixSectionProps = {
  runId: string;
  onExpandChange: (expanded: boolean) => void;
};

function StatCard({
  title,
  value,
  icon: IconComponent,
  color = 'blue',
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
}) {
  return (
    <Box p="2" borderWidth="1px" borderColor="border.default" borderRadius="md" bg="bg.surface">
      <VStack align="start" spacing="1">
        <Box p="1.5" borderRadius="md" bg={`${color}.50`} color={`${color}.600`}>
          <Icon as={IconComponent} boxSize="3.5" />
        </Box>
        <Text fontSize="sm" color="fg.muted" fontWeight="medium">
          {title}
        </Text>
        <Text fontSize="md" fontWeight="bold" color="fg.default">
          {value}
        </Text>
      </VStack>
    </Box>
  );
}

function CoverageProgressCard({
  title,
  covered,
  total,
  percentage,
  color = 'green',
  icon: IconComponent,
}: {
  title: string;
  covered: number;
  total: number;
  percentage: number;
  color?: string;
  icon: React.ElementType;
}) {
  return (
    <Box p="2" borderWidth="1px" borderColor="border.default" borderRadius="md" bg="bg.surface">
      <VStack align="start" spacing="1" w="full">
        <HStack justify="space-between" w="full">
          <Box p="1.5" borderRadius="md" bg={`${color}.50`} color={`${color}.600`}>
            <Icon as={IconComponent} boxSize="3.5" />
          </Box>
          <Text fontSize="xs" color="fg.muted" fontWeight="bold">
            {percentage}%
          </Text>
        </HStack>
        <Text fontSize="sm" color="fg.muted" fontWeight="medium">
          {title}
        </Text>
        <Text fontSize="md" fontWeight="bold" color="fg.default">
          {covered}/{total}
        </Text>
        <Progress value={percentage} size="xs" colorScheme={color} borderRadius="full" w="full" />
      </VStack>
    </Box>
  );
}

function CollapsibleSection({
  title,
  icon: IconComponent,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ElementType;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={onToggle}
        rightIcon={<Icon as={isOpen ? ChevronUp : ChevronDown} boxSize={4} />}
        leftIcon={<Icon as={IconComponent} boxSize={4} />}
        color="fg.muted"
        fontWeight="medium"
        px={0}
        _hover={{ bg: 'transparent', color: 'fg.default' }}
        alignSelf="flex-start"
      >
        {title}
      </Button>
      <Collapse
        in={isOpen}
        animateOpacity
        transition={{ enter: { duration: 0.3 }, exit: { duration: 0.2 } }}
      >
        {children}
      </Collapse>
    </>
  );
}

export function CoverageMatrixContent({
  mappingDocument,
  onExpandChange,
}: {
  mappingDocument: MappingDocumentData;
  onExpandChange: (expanded: boolean) => void;
}) {
  const [open, setOpen] = useState({
    pc: false,
    fs: false,
    pe: false,
    ke: false,
    conditions: false,
  });

  const toggle = (key: keyof typeof open) => {
    const next = { ...open, [key]: !open[key] };
    setOpen(next);
    onExpandChange(Object.values(next).some(Boolean));
  };

  const {
    summary,
    assessmentTaskDefinitions: tasks,
    performanceCriteria,
    foundationSkills,
    performanceEvidence,
    knowledgeEvidence,
    assessmentGaps,
    assessmentConditions,
  } = mappingDocument;

  const coverageColor = summary.assessmentCoveragePercentage === 100 ? 'green' : 'orange';
  const pcsByElement = groupByProperty(performanceCriteria, ({ element }) => element);
  const fsByCategory = groupByProperty(foundationSkills, ({ category }) => category);

  return (
    <Box mt={6}>
      <HStack justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" fontWeight="semibold">
          <Trans>Coverage</Trans>
        </Text>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Download size={14} />}
          onClick={() => downloadMappingDocumentCsv(mappingDocument)}
        >
          <Trans>Download CSV</Trans>
        </Button>
      </HStack>

      <VStack align="stretch" spacing={3}>
        {/* -- Summary stat cards ------ */}
        <Grid templateColumns="repeat(5, 1fr)" gap={2}>
          <CoverageProgressCard
            title={t`Assessment Coverage`}
            covered={summary.pcsCoveredByAssessment}
            total={summary.totalPCs}
            percentage={summary.assessmentCoveragePercentage}
            color={coverageColor}
            icon={CheckCircle}
          />
          <StatCard
            title={t`Foundation Skills`}
            value={summary.totalFS}
            icon={Lightbulb}
            color="gray"
          />
          <StatCard
            title={t`Total Assessments`}
            value={summary.totalAssessments}
            icon={ClipboardList}
            color="gray"
          />
          <StatCard
            title={t`Knowledge Evidence`}
            value={summary.totalKE}
            icon={BookOpen}
            color="gray"
          />
          <StatCard
            title={t`Performance Evidence`}
            value={summary.totalPE}
            icon={Award}
            color="gray"
          />
        </Grid>

        {/* -- Assessment gaps warning ------ */}
        {summary.hasAssessmentGaps && assessmentGaps != null ? (
          <Alert status="warning" fontSize="sm" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              <Trans>{assessmentGaps.length} requirements not covered by assessments</Trans>
            </AlertDescription>
          </Alert>
        ) : null}

        {/* -- Performance Criteria ------ */}
        <CollapsibleSection
          title={t`Performance Criteria`}
          icon={Table2}
          isOpen={open.pc}
          onToggle={() => toggle('pc')}
        >
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Trans>Code</Trans>
                  </Th>
                  <Th>
                    <Trans>Description</Trans>
                  </Th>
                  <Th>{tasks.task1}</Th>
                  <Th>{tasks.task2}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pcsByElement.map(({ key, items }) => [
                  <Tr key={`element-${key}`}>
                    <Td colSpan={4} bg="gray.50" py={1}>
                      <Text fontSize="xs" fontWeight="bold">
                        Element {key}
                      </Text>
                    </Td>
                  </Tr>,
                  ...items.map(({ code, description, assessmentTask1, assessmentTask2 }) => (
                    <Tr key={code}>
                      <Td fontFamily="mono" fontSize="xs">
                        {code}
                      </Td>
                      <Td>
                        <Text fontSize="xs">{description}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" fontFamily="mono">
                          {assessmentTask1.join(', ') || '-'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" fontFamily="mono">
                          {assessmentTask2.join(', ') || '-'}
                        </Text>
                      </Td>
                    </Tr>
                  )),
                ])}
              </Tbody>
            </Table>
          </Box>
        </CollapsibleSection>

        {/* -- Foundation Skills ------ */}
        <CollapsibleSection
          title={t`Foundation Skills`}
          icon={Lightbulb}
          isOpen={open.fs}
          onToggle={() => toggle('fs')}
        >
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Trans>Description</Trans>
                  </Th>
                  <Th>{tasks.task1}</Th>
                  <Th>{tasks.task2}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {fsByCategory.map(({ key, items }) => [
                  <Tr key={`fs-${key}`}>
                    <Td colSpan={3} bg="gray.50" py={1}>
                      <Text fontSize="xs" fontWeight="bold">
                        {key}
                      </Text>
                    </Td>
                  </Tr>,
                  ...items.map(({ description, assessmentTask1, assessmentTask2 }) => (
                    <Tr key={`${key}-${description}`}>
                      <Td>
                        <Text fontSize="xs">{description}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" fontFamily="mono">
                          {assessmentTask1.join(', ') || '-'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" fontFamily="mono">
                          {assessmentTask2.join(', ') || '-'}
                        </Text>
                      </Td>
                    </Tr>
                  )),
                ])}
              </Tbody>
            </Table>
          </Box>
        </CollapsibleSection>

        {/* -- Performance Evidence ------ */}
        <CollapsibleSection
          title={t`Performance Evidence`}
          icon={Award}
          isOpen={open.pe}
          onToggle={() => toggle('pe')}
        >
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Trans>Code</Trans>
                  </Th>
                  <Th>
                    <Trans>Description</Trans>
                  </Th>
                  <Th>{tasks.task1}</Th>
                  <Th>{tasks.task2}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {performanceEvidence.map(
                  ({ tag, description, assessmentTask1, assessmentTask2 }) => (
                    <Tr key={tag}>
                      <Td fontFamily="mono" fontSize="xs">
                        {tag}
                      </Td>
                      <Td>
                        <Text fontSize="xs">{description}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" fontFamily="mono">
                          {assessmentTask1.join(', ') || '-'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" fontFamily="mono">
                          {assessmentTask2.join(', ') || '-'}
                        </Text>
                      </Td>
                    </Tr>
                  ),
                )}
              </Tbody>
            </Table>
          </Box>
        </CollapsibleSection>

        {/* -- Knowledge Evidence ------ */}
        <CollapsibleSection
          title={t`Knowledge Evidence`}
          icon={BookOpen}
          isOpen={open.ke}
          onToggle={() => toggle('ke')}
        >
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Trans>Code</Trans>
                  </Th>
                  <Th>
                    <Trans>Description</Trans>
                  </Th>
                  <Th>{tasks.task1}</Th>
                  <Th>{tasks.task2}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {knowledgeEvidence.map(({ tag, description, assessmentTask1, assessmentTask2 }) => (
                  <Tr key={tag}>
                    <Td fontFamily="mono" fontSize="xs">
                      {tag}
                    </Td>
                    <Td>
                      <Text fontSize="xs">{description}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="xs" fontFamily="mono">
                        {assessmentTask1.join(', ') || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="xs" fontFamily="mono">
                        {assessmentTask2.join(', ') || '-'}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </CollapsibleSection>

        {/* -- Assessment Conditions ------ */}
        <CollapsibleSection
          title={t`Assessment Conditions`}
          icon={ClipboardList}
          isOpen={open.conditions}
          onToggle={() => toggle('conditions')}
        >
          <Box p={3} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {assessmentConditions}
            </Text>
          </Box>
        </CollapsibleSection>
      </VStack>
    </Box>
  );
}

export function CoverageMatrixSection({ runId, onExpandChange }: CoverageMatrixSectionProps) {
  const { data: mappingDocument, isLoading } =
    trpcAdmin.unitsOfCompetency.generationMappingDocument.useQuery({ id: runId });

  if (isLoading || mappingDocument == null) {
    return null;
  }

  return (
    <CoverageMatrixContent mappingDocument={mappingDocument} onExpandChange={onExpandChange} />
  );
}
